import { FC, useEffect, useRef, useState } from 'react';
import {
  DODA_PHOTO_DISPLAY_API_DOMAIN,
  DODA_SITE_URL,
} from '@doda/common/src/constants/urls';
import { corporateClientPhotoSelector } from 'pages/DodaFront/View/JobSearchDetail/redux/features/jobSearchDetail';
import { useSelector } from 'react-redux';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { makeStaticUrl } from '@doda/common/src/logics/staticUrl';
import { measureClickPublicationPhotoLink } from 'logics/analytics/clickEventModule/publicationPhoto';
import { VisibleAnalytics } from '@doda/common/src/components/Analytics/VisibleAnalytics';
import {
  measureVisiblePublicationPhotoArea,
  measureVisiblePublicationPhotoLink,
} from 'logics/analytics/visibleModules/publicationPhoto';
import { PhotoAreaProps } from 'pages/DodaFront/View/JobSearchDetail/components/Body/JobDetail/CompanyOverview';
import { useTab } from 'pages/DodaFront/View/JobSearchDetail/components/TabArea/hooks/useTab';
import { TAB_PHOTOS } from '@doda/common/src/constants/properties/const';
import styles from './index.module.scss';

// 画面に表示する写真(画像)枚数
const DISPLAY_LENGTH = 3;

/**
 * 写真一覧コンポーネント
 */
export const PublicationPhotoArea: FC<PhotoAreaProps> = ({
  jid,
  initialTab,
  wcnsId,
  isApplied,
}) => {
  const publicationPhotos = useSelector(corporateClientPhotoSelector);
  const {
    mpsc_sid,
    tp,
    bnid,
    osscfl,
    scoutlist_id,
    message_id,
    record_scoutlist_id,
    record_message_id,
    ap_id,
    recommendID,
  } = useSelector(appQueryParamsSelector);

  const staticurl = makeStaticUrl(
    {
      jid,
      tab: 'photos',
      fm: 'jobdetail',
      mpsc_sid,
      tp,
      bnid,
      bnnt: bnid ? '1' : undefined,
      osscfl,
      scoutlist_id,
      message_id,
      record_scoutlist_id,
      record_message_id,
      ap_id,
    },
    DODA_SITE_URL.jobSearchDetail
  );
  const { onChangeTab } = useTab({ initialTab, jid, wcnsId });
  const [loadedImages, setLoadedImages] = useState<number[]>([]);
  const itemRefs = useRef<(HTMLImageElement | null)[]>([]);

  useEffect(() => {
    itemRefs.current.forEach((item, idx) => {
      if (item) {
        item.decode().then(() => {
          setLoadedImages((prevState) => [...prevState, idx]);
        });
      }
    });
  }, []);

  if (!publicationPhotos || publicationPhotos.length <= 0) {
    return <></>;
  }
  const photosUrl = `${staticurl}${recommendID ? `?recommendID=${recommendID}` : ''}`;
  const displayMoreLink = publicationPhotos.length > DISPLAY_LENGTH;

  const handleClickMorePhotos = (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();
    if (photosUrl) {
      onChangeTab(TAB_PHOTOS, isApplied);
    }
    window.scrollTo({ top: 0, behavior: 'instant' }); // 画面をスクロール
  };

  // 写真エリア
  return (
    <section className={`${styles.imageGallery__area}`}>
      <VisibleAnalytics onVisible={() => measureVisiblePublicationPhotoArea()}>
        <div className={styles.imageGallery}>
          {publicationPhotos.slice(0, DISPLAY_LENGTH).map((item, idx) => (
            <div
              className={`${styles.imageGallery__imageWrap} ${loadedImages.includes(idx) ? '' : styles.unloaded}`.trim()}
              key={idx}
            >
              <img
                src={createImagePath(item)}
                alt=""
                className={styles.imageGallery__image}
                width="293"
                height="293"
                loading="lazy"
                ref={(el) => {
                  itemRefs.current[idx] = el;
                }}
              />
            </div>
          ))}
        </div>
        {displayMoreLink && (
          <VisibleAnalytics
            onVisible={() => measureVisiblePublicationPhotoLink()}
          >
            <div className={styles.displayLink__Wrap}>
              <a
                href="#"
                className={styles.displayLink}
                onClick={(event) => {
                  handleClickMorePhotos(event);
                  measureClickPublicationPhotoLink();
                }}
              >
                <span>写真をもっと見る</span>
              </a>
            </div>
          </VisibleAnalytics>
        )}
      </VisibleAnalytics>
    </section>
  );
};

/**
 * 画像表示URLを返す.
 *
 * @param imagePath 画像表示APIのパス
 * @returns 画像表示URL
 */
const createImagePath = (imagePath: string) => {
  const env = process.env.APP_ENV as string;
  let domain = DODA_PHOTO_DISPLAY_API_DOMAIN[env];
  if (!domain) {
    const { host } = window.location;
    // ホスト名とドメインのマッピング
    const hostToDomainMap: { [key: string]: string } = {
      'doda.jp': 'https://src.doda.jp',
      'test-staging.doda.jp': 'https://test-src.doda.jp',
      'test-st.doda.jp': 'https://test-st.doda.jp',
    };
    // それ以外の場合はデフォルトの処理としてホスト名を使用
    domain = hostToDomainMap[host] || `https://${host}`;
  }
  return `${domain}${imagePath}`;
};
